import { Injectable } from '@angular/core';
import { QueryResultWrapper, RagQueryResult } from '../../../models/chat.model';
import { ChatItem, ChatQueryUiResult } from '../../../models/chat-ui.model';
import { ChatSourceMapperService } from './chat-source-mapper.service';
import { ChatGroundingMapperService } from './chat-grounding-mapper.service';
import { LanguageService } from '../../language/language.service';

@Injectable({
  providedIn: 'root'
})
export class ChatQueryUiMapperService {
  constructor(
    private sourceMapper: ChatSourceMapperService,
    private grounding: ChatGroundingMapperService,
    private language: LanguageService
  ) {}

  mapQueryResult(
    result: QueryResultWrapper,
    messageContent: string,
    entityIdFactory: () => string,
    fallbackSelectedChunkId: string | null
  ): ChatQueryUiResult {
    if (result.type === 'rag' && result.ragResult && Array.isArray(result.ragResult.relevant_chunks)) {
      return this.mapRagResult(result, entityIdFactory, fallbackSelectedChunkId);
    }

    if (result.type === 'csv' && result.csvResult && Array.isArray(result.csvResult.csv_documents)) {
      return {
        kind: 'csv',
        sources: this.sourceMapper.mapCsvDocuments(result.csvResult.csv_documents),
        csvDocuments: result.csvResult.csv_documents,
        pendingQuery: messageContent,
        showCsvModal: true
      };
    }

    if (result.type === 'generation_proposal' && result.generationProposalResult) {
      const proposal = result.generationProposalResult;
      const unnamed = this.language.translate('chat.generation.unnamedDoc');
      const suggestedSources = (proposal.suggested_sources || []).map((source) => ({
        documentId: source.document_id,
        // Si no hay nombre real (llega el propio id/uuid), mostramos una etiqueta genérica.
        filename: source.original_filename && source.original_filename !== source.document_id
          ? source.original_filename
          : unnamed
      }));
      const hasSources = suggestedSources.length > 0;
      const message = this.buildSystemMessage(
        hasSources
          ? this.language.translate('chat.generation.candidatesQuestion')
          : (proposal.proposal_text || this.language.translate('chat.generation.proposal.fallback')),
        entityIdFactory()
      );
      // Estado para abrir el wizard /generate PRECARGADO (fuentes + mensaje + experto + idioma).
      const routeState = {
        suggestedSources,
        message: proposal.message ?? null,
        expertRole: proposal.suggested_expert_role ?? null,
        language: proposal.language ?? null
      };
      if (hasSources) {
        // Mostramos la lista de documentos candidatos y pedimos confirmación.
        message.candidateDocuments = suggestedSources;
        message.actions = [
          // Confirmar: encola la generación y navega a "Mis Resúmenes".
          {
            kind: 'generate',
            label: this.language.translate('chat.generation.proposal.confirm'),
            generate: {
              sourceRefs: suggestedSources.map((source) => source.documentId),
              language: proposal.language ?? null,
              expertRole: proposal.suggested_expert_role ?? null,
              message: proposal.message ?? null,
              navigateToSummaries: true
            }
          },
          // Ajustar: abre el asistente para cambiar la selección de documentos.
          {
            kind: 'route',
            label: this.language.translate('chat.generation.proposal.adjust'),
            route: '/generate',
            routeState
          }
        ];
      } else {
        // Sin documentos inferidos: solo ofrecemos abrir el asistente para elegirlos manualmente.
        message.actions = [{
          kind: 'route',
          label: this.language.translate('chat.generation.proposal.button'),
          route: '/generate',
          routeState
        }];
      }
      return { kind: 'system-message', message };
    }

    if (result.type === 'unsupported' && result.unsupportedResult) {
      return {
        kind: 'system-message',
        message: this.buildSystemMessage(
          this.language.translate('chat.error.unsupportedQuestion'),
          entityIdFactory()
        )
      };
    }

    if (result.type === 'error') {
      const error = result.error || this.language.translate('chat.error.unexpected');
      return {
        kind: 'error',
        error,
        message: this.buildSystemMessage(error, entityIdFactory())
      };
    }

    return {
      kind: 'system-message',
      message: this.buildSystemMessage(this.language.translate('chat.error.unknownResponse'), entityIdFactory())
    };
  }

  private mapRagResult(
    result: QueryResultWrapper,
    entityIdFactory: () => string,
    fallbackSelectedChunkId: string | null
  ): ChatQueryUiResult {
    const ragResult = result.ragResult!;
    const selectedCandidates = this.sourceMapper.getSelectedCandidatesFromRagResult(ragResult);
    const selectedState = this.sourceMapper.markAndMoveSelectedFirst(
      this.sourceMapper.mapRagChunks(ragResult.relevant_chunks || []),
      selectedCandidates,
      fallbackSelectedChunkId
    );
    const serverMessageId = this.getServerMessageId(result);
    const message: ChatItem = {
      type: 'system',
      content: ragResult.response,
      timestamp: new Date(),
      sources: selectedState.sources,
      entityId: entityIdFactory()
    };

    if (serverMessageId) {
      message.id = String(serverMessageId);
    }

    this.attachGrounding(message, ragResult);

    return {
      kind: 'rag',
      message,
      sources: selectedState.sources,
      selectedSource: selectedState.selectedSource,
      selectedChunkId: selectedState.selectedChunkId,
      // Abrir el panel depende de que haya fuentes que mostrar, NO de que alguna esté
      // seleccionada: si colgara de `hasSelection`, el panel dejaría de abrirse justo en
      // multi-doc (donde ya no coronamos nada), que es cuando más hay para ver.
      showDetails: selectedState.sources.length > 0,
      sessionId: result.sessionId || null,
      appliedExpertRole: this.getAppliedExpertRole(ragResult)
    };
  }

  /**
   * Adjunta el `evidence_status` aditivo (Fase 4) al mensaje del asistente. Es opcional:
   * solo se asigna si el backend lo envió, de modo que la vista degrada al comportamiento
   * actual cuando falta.
   */
  private attachGrounding(message: ChatItem, ragResult: RagQueryResult): void {
    const evidenceStatus = this.grounding.mapEvidenceStatus(ragResult.evidence_status);
    if (evidenceStatus) {
      message.evidenceStatus = evidenceStatus;
    }
  }

  private getAppliedExpertRole(ragResult: RagQueryResult): string | null {
    const value = ragResult.applied_expert_role;
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private buildSystemMessage(content: string, entityId: string): ChatItem {
    return {
      type: 'system',
      content,
      timestamp: new Date(),
      entityId
    };
  }

  private getServerMessageId(result: QueryResultWrapper): string | null {
    const ragResult = result.ragResult as Record<string, unknown> | undefined;
    const wrapper = result as unknown as Record<string, unknown>;
    const candidate =
      ragResult?.['message_id'] ||
      ragResult?.['id'] ||
      ragResult?.['answer_id'] ||
      wrapper['messageId'] ||
      wrapper['id'];

    return candidate === undefined || candidate === null ? null : String(candidate);
  }
}
